<template>
  <b-form-group
    class="control-datepicker"
    :label="control.label"
    :label-for="control.id"
    :label-sr-only="control.labelSrOnly"
    :label-cols="control.labelCols"
    :label-cols-sm="control.labelColsSM"
    :label-cols-md="control.labelColsMD"
    :label-cols-lg="control.labelColsLG"
    :label-cols-xl="control.labelColsXL"
    :label-align="control.labelAlign"
    :label-align-sm="control.labelAlignSM"
    :label-align-md="control.labelAlignMD"
    :label-align-lg="control.labelAlignLG"
    :label-align-xl="control.labelAlignXL"
    :class="formGroupClasses"
    :description="control.description"
    :state="controlState"
  >
    <div class="position-relative">
      <b-input-group
        :size="control.size"
        :class="inputGroupClasses"
      >
        <template v-if="$slots.prepend" #prepend><slot name="prepend"/></template>

        <div class="control-wrapper">
          <b-form-datepicker
            :id="control.id"
            :name="control.name"
            :form="control.form"
            :type="control.inputType"
            v-model="control.value"
            :placeholder="control.placeholder"
            :size="control.size"
            :class="controlClasses"
            :locale="control.locale"
            :startWeekday="control.startWeekday"
            :dateFormatOptions="control.dateFormatOptions"
            :hideHeader="control.hideHeader"
            :min="control.min"
            :max="control.max"
            :dateDisabledFn="control.dateDisabledFn"
            :noFlip="control.noFlip"
            :noHighlightToday="control.noHighlightToday"
            :resetButton="control.resetButton"
            :labelResetButton="control.labelResetButton"
            :readonly="control.readonly"
            :disabled="control.disabled"
            :state="controlState"
            :autofocus="control.autofocus"
            @input="onControlInput"
            @blur="onControlChange(control.value)"
            @focus.native.capture="control.hasFocus = true"
            @blur.native.capture="control.hasFocus = false"
          >
            <template #button-content>
              <b-icon icon="calendar"/>
            </template>
          </b-form-datepicker>
          <label v-if="!control.labelHide" :for="control.id" aria-hidden="true">{{ control.label }}</label>
        </div>

        <template v-if="$slots.append" #append><slot name="append"/></template>
      </b-input-group>
    </div>

    <b-form-valid-feedback v-if="control.feedbacksValid" :state="controlState">{{ control.feedbacksValid }}</b-form-valid-feedback>
    <b-form-invalid-feedback :state="controlState">{{ controlInvalidFeedbacks }}</b-form-invalid-feedback>
  </b-form-group>
</template>

<script>
import { guid } from '@/assets/js/helper/guid'
import store from '@/$plugins/store'

export default {
  name: 'ControlInput',
  props: {
    id: { type: String, default: null },
    name: { type: String, default: '' },
    form: { type: String, default: '' },
    inputType: { type: String, default: 'text' },
    formGroupClass: { type: [String, Object, Array], default: '' },
    inputGroupClass: { type: [String, Object, Array], default: '' },
    controlClass: { type: [String, Object, Array], default: '' },
    tabindex: { type: [String, Number], default: null },
    label: { type: String, default: '' },
    labelSrOnly: { type: Boolean, default: true },
    labelHide: { type: Boolean, default: false },
    labelCols: { type: [Number, String, Boolean], default: null },
    labelColsSM: { type: [Number, String, Boolean], default: null },
    labelColsMD: { type: [Number, String, Boolean], default: null },
    labelColsLG: { type: [Number, String, Boolean], default: null },
    labelColsXL: { type: [Number, String, Boolean], default: null },
    labelAlign: { type: String, default: '' },
    labelAlignSM: { type: String, default: '' },
    labelAlignMD: { type: String, default: '' },
    labelAlignLG: { type: String, default: '' },
    labelAlignXL: { type: String, default: '' },
    value: { type: [String, Number, Array, Object], default: null },
    placeholder: { type: String, default: '' },
    description: { type: String, default: '' },
    size: { type: String, default: '' },
    locale: { type: String, default: store.getters['gui/language:get'] },
    startWeekday: { type: [Number, String], default: store.getters['gui/getWeekStartsOn'] },
    dateFormatOptions: { type: Object, default: () => ({ year: 'numeric', month: 'long', day: 'numeric' }) },
    hideHeader: { type: Boolean, default: true },
    min: { type: [Date, String], default: null },
    max: { type: [Date, String], default: null },
    dateDisabledFn: { type: Function, default: null },
    noFlip: { type: Boolean, default: false },
    noHighlightToday: { type: Boolean, default: false },
    resetButton: { type: Boolean, default: true },
    labelResetButton: { type: String, default: '⨉' },
    dirty: { type: Boolean, default: false },
    state: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
    validations: { type: Object, default: () => ({}) },
    feedbacksValid: { type: String, default: '' },
    feedbacksInvalid: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      control: {
        id: this.$props.id || guid(),
        name: this.$props.name,
        form: this.$props.form,
        inputType: this.$props.inputType,
        formGroupClass: this.$props.formGroupClass,
        inputGroupClass: this.$props.inputGroupClass,
        controlClass: this.$props.controlClass,
        tabindex: this.$props.tabindex,
        label: this.$props.label,
        labelSrOnly: this.$props.labelSrOnly,
        labelHide: this.$props.labelHide,
        labelCols: this.$props.labelCols,
        labelColsSM: this.$props.labelColsSM,
        labelColsMD: this.$props.labelColsMD,
        labelColsLG: this.$props.labelColsLG,
        labelColsXL: this.$props.labelColsXL,
        labelAlign: this.$props.labelAlign,
        labelAlignSM: this.$props.labelAlignSM,
        labelAlignMD: this.$props.labelAlignMD,
        labelAlignLG: this.$props.labelAlignLG,
        labelAlignXL: this.$props.labelAlignXL,
        value: this.$props.value,
        placeholder: this.$props.placeholder,
        description: this.$props.description,
        size: this.$props.size,
        locale: this.$props.locale,
        startWeekday: this.$props.startWeekday,
        dateFormatOptions: this.$props.dateFormatOptions,
        hideHeader: this.$props.hideHeader,
        min: this.$props.min,
        max: this.$props.max,
        dateDisabledFn: this.$props.dateDisabledFn,
        noFlip: this.$props.noFlip,
        noHighlightToday: this.$props.noHighlightToday,
        resetButton: this.$props.resetButton,
        labelResetButton: this.$props.labelResetButton,
        dirty: this.$props.dirty,
        state: this.$props.state,
        readonly: this.$props.readonly,
        disabled: this.$props.disabled,
        autofocus: this.$props.autofocus,
        validations: this.$props.validations,
        feedbacksValid: this.$props.feedbacksValid,
        feedbacksInvalid: this.$props.feedbacksInvalid,
        hasFocus: false
      }
    }
  },
  validations () {
    return {
      control: {
        value: this.control.validations
      }
    }
  },
  computed: {
    validator () {
      return this.$v.control.value
    },
    controlCanValidate () {
      if (this.validator !== undefined) return this.validator.$model !== undefined ? Object.keys(this.validator.$params).length > 0 : false
      return false
    },
    controlState () {
      if (this.controlCanValidate) return this.validator.$dirty ? !this.validator.$error : null
      if (this.control.state !== null) return this.control.dirty ? this.control.state : null
      return this.control.dirty ? true : null
    },
    controlInvalidFeedbacks () {
      if (this.controlCanValidate) {
        const validations = Object.keys(this.validator.$params || {})
        const feedbacks = this.feedbacksInvalid || {}
        const error = validations.find(key => this.validator[key] === false) || ''

        return this.validator.$error ? feedbacks[error] instanceof Function ? feedbacks[error]() : feedbacks[error] || '' : ''
      }

      if (this.control.dirty && !this.control.state) {
        return this.feedbacksInvalid.error || ''
      }

      return ''
    },
    formGroupClasses () {
      return [
        {
          'has-focus': this.control.hasFocus,
          'has-prepend': this.$slots.prepend,
          'has-append': this.$slots.append,
          'is-filled': this.control.value !== undefined && this.control.value !== null && this.control.value !== '',
          'is-readonly': this.control.readonly,
          'is-disabled': this.control.disabled,
          'is-dirty': this.controlCanValidate ? this.validator.$dirty : false
        }
      ].concat(this.control.formGroupClass || [])
    },
    inputGroupClasses () {
      return [
        {
          'mb-0': this.control.label === ''
        }
      ].concat(this.control.formInputClass || [])
    },
    controlClasses () {
      return [].concat(this.control.controlClass || [])
    }
  },
  methods: {
    setDirty (dirty) {
      if (this.controlCanValidate) {
        if (dirty) {
          this.validator.$touch()
        } else {
          this.validator.$reset()
        }
      }
    },
    onControlInput (controlValue) {
      if (this.controlCanValidate && this.validator.$dirty) {
        this.validator.$model = controlValue
        this.validator.$touch()
      }

      this.control.state = null

      this.$emit('input', controlValue)
    },
    onControlChange (controlValue) {
      if (this.controlCanValidate) {
        this.validator.$model = controlValue
        this.validator.$touch()
      }

      this.control.state = null

      this.$emit('change', controlValue)
    },
    setTabindex () {
      this.$el.querySelector('button').setAttribute('tabindex', this.control.tabindex)
    }
  },
  created () {
    this.setDirty(this.control.dirty)
  },
  mounted () {
    this.$nextTick(() => {
      this.setTabindex()
    })
  },
  watch: {
    '$props.id' (id) { this.control.id = id },
    '$props.name' (name) { this.control.name = name },
    '$props.form' (form) { this.control.form = form },
    '$props.inputType' (inputType) { this.control.inputType = inputType },
    '$props.formGroupClass' (formGroupClass) { this.control.formGroupClass = formGroupClass },
    '$props.inputGroupClass' (inputGroupClass) { this.control.inputGroupClass = inputGroupClass },
    '$props.controlClass' (controlClass) { this.control.controlClass = controlClass },
    '$props.tabindex' (tabindex) { this.control.tabindex = tabindex },
    '$props.label' (label) { this.control.label = label },
    '$props.labelSrOnly' (labelSrOnly) { this.control.labelSrOnly = labelSrOnly },
    '$props.labelHide' (labelHide) { this.control.labelHide = labelHide },
    '$props.labelCols' (labelCols) { this.control.labelCols = labelCols },
    '$props.labelColsSM' (labelColsSM) { this.control.labelColsSM = labelColsSM },
    '$props.labelColsMD' (labelColsMD) { this.control.labelColsMD = labelColsMD },
    '$props.labelColsLG' (labelColsLG) { this.control.labelColsLG = labelColsLG },
    '$props.labelColsXL' (labelColsXL) { this.control.labelColsXL = labelColsXL },
    '$props.labelAlign' (labelAlign) { this.control.labelAlign = labelAlign },
    '$props.labelAlignSM' (labelAlignSM) { this.control.labelAlignSM = labelAlignSM },
    '$props.labelAlignMD' (labelAlignMD) { this.control.labelAlignMD = labelAlignMD },
    '$props.labelAlignLG' (labelAlignLG) { this.control.labelAlignLG = labelAlignLG },
    '$props.labelAlignXL' (labelAlignXL) { this.control.labelAlignXL = labelAlignXL },
    '$props.value' (value) { this.control.value = value },
    '$props.placeholder' (placeholder) { this.control.placeholder = placeholder },
    '$props.description' (description) { this.control.description = description },
    '$props.size' (size) { this.control.size = size },
    '$props.locale' (locale) { this.control.locale = locale },
    '$props.startWeekday' (startWeekday) { this.control.startWeekday = startWeekday },
    '$props.dateFormatOptions' (dateFormatOptions) { this.control.dateFormatOptions = dateFormatOptions },
    '$props.hideHeader' (hideHeader) { this.control.hideHeader = hideHeader },
    '$props.min' (min) { this.control.min = min },
    '$props.max' (max) { this.control.max = max },
    '$props.dateDisabledFn' (dateDisabledFn) { this.control.dateDisabledFn = dateDisabledFn },
    '$props.noFlip' (noFlip) { this.control.noFlip = noFlip },
    '$props.noHighlightToday' (noHighlightToday) { this.control.noHighlightToday = noHighlightToday },
    '$props.resetButton' (resetButton) { this.control.resetButton = resetButton },
    '$props.labelResetButton' (labelResetButton) { this.control.labelResetButton = labelResetButton },
    '$props.dirty' (dirty) {
      this.control.dirty = dirty
      this.setDirty(this.control.dirty)
    },
    '$props.state' (state) { this.control.state = state },
    '$props.readonly' (readonly) { this.control.readonly = readonly },
    '$props.disabled' (disabled) { this.control.disabled = disabled },
    '$props.autofocus' (autofocus) { this.control.autofocus = autofocus },
    '$props.validations' (validations) { this.control.validations = validations },
    '$props.feedbacksValid' (feedbacksValid) { this.control.feedbacksValid = feedbacksValid },
    '$props.feedbacksInvalid' (feedbacksInvalid) { this.control.feedbacksInvalid = feedbacksInvalid },
    'control.tabindex' (tabindex) { this.setTabindex() }
  }
}
</script>

<style lang="scss">
$controldatepicker-calendar-grid-btn-margin: 2px !default;
$controldatepicker-calendar-grid-btn-size: 24px !default;
$controldatepicker-calendar-grid-btn-font-size: $font-size-sm !default;

.control-datepicker {
  .b-form-btn-label-control.form-control {
    > .btn {
      padding-left: 0;
    }

    > .dropdown-menu {}

    .b-calendar {
      .b-calendar-inner {
        min-width: 0;
        width: 100% !important;

        .b-calendar-grid-body {
          .col {
            &[data-date] {
              .btn {
                display: flex;
                justify-content: center;
                align-items: center;
                margin: $controldatepicker-calendar-grid-btn-margin;
                padding: 0;
                width: $controldatepicker-calendar-grid-btn-size;
                height: $controldatepicker-calendar-grid-btn-size;
                font-size: $controldatepicker-calendar-grid-btn-font-size;
              }
            }
          }
        }
      }
    }
  }
}
</style>
